import React from "react";
import { formatDateReadable } from "../../utilities/helpers";
import DescriptionIcon from "@mui/icons-material/Description";
import BadgeIcon from "@mui/icons-material/Badge";
import DrawOutlinedIcon from "@mui/icons-material/DrawOutlined";
import QuizOutlinedIcon from "@mui/icons-material/QuizOutlined";
import ChecklistIcon from "@mui/icons-material/Checklist";
import RateReviewOutlinedIcon from "@mui/icons-material/RateReviewOutlined";
import ConnectWithoutContactIcon from "@mui/icons-material/ConnectWithoutContact";

// Shared helpers for LMS record rows on the employee profile. The combined
// certificateData array carries real certificates plus seven LMS record
// pseudo-types; the Documents tab renders the file-backed kinds and the
// Training tab renders the data-backed kinds, so the type constants, labels,
// icon/date helpers, and row shells live here - one source, no drift.

// LMS record types that carry a downloadable file (Documents tab).
export const LMS_FILE_TYPES = ["signature_document", "document_upload"];
// LMS record types that carry data but no file (Training tab).
export const LMS_DATA_TYPES = [
  "quiz_result",
  "checkpoint_signoff",
  "manager_input",
  "form_submission",
  "roleplay_result",
];

// Display labels for the LMS pseudo-types. The Documents tab merges its
// fetched certificate-type catalog over these; the Training tab uses them
// directly (its five types are never in the catalog).
export const LMS_RECORD_LABELS = {
  signature_document: "Signed Document",
  document_upload: "Uploaded Document",
  quiz_result: "Quiz Result",
  checkpoint_signoff: "Checkpoint Sign-off",
  manager_input: "Manager Input",
  form_submission: "Form Submission",
  roleplay_result: "Practice Scenario",
};

export const getRecordIcon = (type) => {
  switch (type) {
    case "food_permit":
    case "liquor_permit":
      return BadgeIcon;
    case "signature_document":
      return DrawOutlinedIcon;
    case "quiz_result":
      return QuizOutlinedIcon;
    case "checkpoint_signoff":
      return ChecklistIcon;
    case "manager_input":
      return RateReviewOutlinedIcon;
    case "roleplay_result":
      return ConnectWithoutContactIcon;
    case "break_waiver":
    case "document_upload":
    case "form_submission":
    default:
      return DescriptionIcon;
  }
};

export const getDateLabel = (type) => {
  switch (type) {
    case "signature_document":
      return "Signed";
    case "document_upload":
      return "Uploaded";
    case "quiz_result":
    case "roleplay_result":
      return "Completed";
    case "checkpoint_signoff":
      return "Signed off";
    case "manager_input":
      return "Recorded";
    case "form_submission":
      return "Submitted";
    default:
      return "Expiration";
  }
};

export const recordDateText = (record) => {
  if (LMS_FILE_TYPES.includes(record.type) || LMS_DATA_TYPES.includes(record.type)) {
    return record.created_at
      ? formatDateReadable(new Date(record.created_at).toISOString().split("T")[0])
      : "Unknown date";
  }
  return record.expiration_date ? formatDateReadable(record.expiration_date) : null;
};

export const RowShell = ({ leading, children, actions }) => (
  <div className="flex items-center gap-3 px-4 py-3 hover:bg-slate-50 transition-colors">
    {leading}
    <div className="flex-1 min-w-0">{children}</div>
    <div className="flex items-center gap-0.5 shrink-0">{actions}</div>
  </div>
);

export const IconThumb = ({ type }) => {
  const IconComponent = getRecordIcon(type);
  return (
    <div className="w-11 h-11 shrink-0 rounded-lg bg-violet-50 border border-violet-100 flex items-center justify-center">
      <IconComponent className="text-violet-600" style={{ fontSize: 20 }} />
    </div>
  );
};
